import * as React from "react";
import Box from "@mui/material/Box";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { useTranslation } from "react-i18next";
import { set } from "../../../app/globalSlice";
import { requestApi } from "../../../helpers/RequestApi";
import { DELETE, GET, PATCH, POST } from "../../../utils/MethodUtils";
import { PRINTER_TEMPLATES_URL, PRINTERS_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import Typography from "@mui/material/Typography";
import SearchComponent from "../SearchComponent";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import {
  PrinterInterface,
  PrinterTemplateInterface,
} from "../../../interfaces/PrinterInterface";
import { InputInterface } from "../../../interfaces/InputInterface";
import { DialogProps, TextareaAutosize, TextField } from "@mui/material";
import CopyClipboardComponent from "../CopyClipboardComponent";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import getErrorApi from "../../../helpers/GetErrorApi";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import PrintComponent from "./PrintComponent";
import PrintEmplacementsComponent from "./PrintEmplacementsComponent";

const slugify = require("slugify");

interface State {
  printer: PrinterInterface;
}

interface State2 {
  printerTemplate: PrinterTemplateInterface | undefined;
  index: number;
}

interface FormState {
  name: InputInterface;
  ip: InputInterface;
  port: InputInterface;
}

interface FormState2 {
  name: InputInterface;
  content: InputInterface;
  code: InputInterface;
}

const PrinterTemplateFormComponent: React.FC<State2> = React.memo(
  ({ printerTemplate, index }) => {
    const [loading, setLoading] = React.useState(false);
    const [deleteDialog, setDeleteDialog] = React.useState(false);
    const printerTemplates = useAppSelector(
      (state: RootState) => state.globalState.printerTemplates
    );
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("sm");
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const token = useAppSelector((state: RootState) => state.globalState.token);

    const getDefaultValues = React.useCallback((): FormState2 => {
      return {
        name: {
          value: printerTemplate?.name,
          error: "",
        },
        content: {
          value: printerTemplate?.content,
          error: "",
        },
        code: {
          value: printerTemplate?.code,
          error: "",
        },
      };
    }, [
      printerTemplate?.code,
      printerTemplate?.content,
      printerTemplate?.name,
    ]);
    const [values, setValues] = React.useState<FormState2>(getDefaultValues());
    const [queries, setQueries] = React.useState<any>([]);

    const handleChange = React.useCallback(
      (prop: keyof FormState2) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setValues((v) => {
            return {
              ...v,
              ...(prop === "name" && {
                code: { ...v.code, value: event.target.value, error: "" },
              }),
              [prop]: { ...v[prop], value: event.target.value, error: "" },
            };
          });
        },
      []
    );

    const handleChangetextArea = React.useCallback(
      (prop: keyof FormState2) =>
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
          setValues((v) => {
            return {
              ...v,
              [prop]: { ...v[prop], value: event.target.value, error: "" },
            };
          });
        },
      []
    );

    const save = React.useCallback(async () => {
      setLoading(true);
      let method = POST;
      let path = PRINTER_TEMPLATES_URL;
      if (printerTemplate?.id) {
        method = PATCH;
        path += "/" + printerTemplate?.id;
      }
      const response = await requestApi({
        method: method,
        path: path,
        allowError: true,
        token: token,
        body: {
          name: values.name.value,
          content: values.content.value,
          ...(!printerTemplate?.id && {
            code: values.code.value,
            deletable: printerTemplate?.deletable ?? true,
          }),
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        const newPrinterTemplates = [...(printerTemplates ?? [])];
        const index = newPrinterTemplates.findIndex(
          (x) => x?.code === printerTemplate?.code
        );
        newPrinterTemplates[index] = response.content;
        dispatch(set({ printerTemplates: newPrinterTemplates }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      dispatch,
      printerTemplate?.code,
      printerTemplate?.deletable,
      printerTemplate?.id,
      printerTemplates,
      t,
      token,
      values.code.value,
      values.content.value,
      values.name.value,
    ]);

    const deletePrinterTemplate = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: DELETE,
        path: PRINTER_TEMPLATES_URL + "/" + printerTemplate?.id,
        allowError: true,
        token: token,
      });
      if (response.statusCode === 204) {
        const newPrinterTemplates = [...(printerTemplates ?? [])];
        const index = newPrinterTemplates.findIndex(
          (x) => x?.code === printerTemplate?.code
        );
        newPrinterTemplates[index] = undefined;
        dispatch(set({ printerTemplates: newPrinterTemplates }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      dispatch,
      printerTemplate?.code,
      printerTemplate?.id,
      printerTemplates,
      t,
      token,
    ]);

    const onClickDelete = React.useCallback(async () => {
      if (!printerTemplate?.id) {
        const newPrinterTemplates = [...(printerTemplates ?? [])];
        newPrinterTemplates[index] = undefined;
        dispatch(set({ printerTemplates: newPrinterTemplates }));
      } else {
        setDeleteDialog(true);
      }
    }, [dispatch, index, printerTemplate?.id, printerTemplates]);

    const handleClose = React.useCallback(() => {
      setDeleteDialog(false);
    }, []);

    React.useEffect(() => {
      setValues(getDefaultValues());
    }, [printerTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
      setValues((v) => {
        return {
          ...v,
          code: {
            ...v.code,
            value: slugify(
              (v.code.value ?? "")
                .replace(" ", "_")
                .replace(/[^A-Za-z0-9_]+/g, "")
            ).toUpperCase(),
            error: "",
          },
        };
      });
    }, [values.code.value]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
      // @ts-ignore
      const matches = [...new Set((values.content.value ?? "").match(/:[A-Za-z]+/g))];
      setQueries(
        matches.map((match) => {
          return {
            prop: match.slice(1),
            value: "",
          };
        })
      );
    }, [values.content.value]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        {printerTemplate && (
          <>
            <Dialog
              maxWidth={maxWidth}
              fullWidth={true}
              fullScreen={false}
              onClose={handleClose}
              open={deleteDialog}
            >
              <DialogContent>{t("word.deletePrinterTemplate")}</DialogContent>
              <DialogActions sx={{ justifyContent: "space-between" }}>
                <Button onClick={handleClose}>{t("word.no")}</Button>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  onClick={deletePrinterTemplate}
                >
                  {t("word.yes")}
                </LoadingButton>
              </DialogActions>
            </Dialog>
            <TableRow>
              <TableCell>
                <TextField
                  autoComplete="off"
                  error={!!values.name.error}
                  helperText={t(values.name.error ?? "")}
                  sx={{ width: "100%" }}
                  type="text"
                  value={values.name.value}
                  onChange={handleChange("name")}
                  label={t("field.name")}
                />
              </TableCell>
              <TableCell>
                <TextareaAutosize
                  minRows={4}
                  value={values.content.value}
                  style={{ width: "100%" }}
                  onChange={handleChangetextArea("content")}
                />
              </TableCell>
              <TableCell>
                {printerTemplate.id ? (
                  <CopyClipboardComponent text={printerTemplate?.code ?? ""} />
                ) : (
                  <TextField
                    autoComplete="off"
                    error={!!values.code.error}
                    helperText={t(values.code.error ?? "")}
                    sx={{ width: "100%" }}
                    type="text"
                    value={values.code.value}
                    onChange={handleChange("code")}
                    label={t("word.code")}
                  />
                )}
              </TableCell>
              <TableCell>
                <LoadingButton
                  variant="text"
                  color="inherit"
                  sx={{
                    borderRadius: "50%",
                    minWidth: "auto",
                    padding: "8px",
                    color: "rgba(0, 0, 0, 0.54)",
                  }}
                  loading={loading}
                  onClick={save}
                >
                  <SaveIcon />
                </LoadingButton>
                {printerTemplate && printerTemplate.deletable && (
                  <IconButton size="large" onClick={onClickDelete}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </TableCell>
              <TableCell>
                {printerTemplate && printerTemplate.id && (
                  <>
                    <PrintComponent
                      printer="DEPOT"
                      params={queries.reduce(
                        (
                          acc: Record<string, string>,
                          { prop, value }: { prop: string; value: string }
                        ) => {
                          acc[prop] = value;
                          return acc;
                        },
                        {}
                      )}
                      template={printerTemplate.code}
                    />
                    {queries.map((query: any) => {
                      return (
                        <TextField
                          key={query.prop}
                          autoComplete="off"
                          sx={{ width: "100%", marginTop: 1 }}
                          type="text"
                          value={query.value}
                          onChange={(e) => {
                            setQueries((q: any) => {
                              const item = q.find(
                                (x: any) => x.prop === query.prop
                              );
                              item.value = e.target.value;
                              return [...q];
                            });
                          }}
                          label={query.prop}
                        />
                      );
                    })}
                  </>
                )}
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    );
  }
);

const PrinterFormComponent: React.FC<State> = React.memo(({ printer }) => {
  const [loading, setLoading] = React.useState(false);
  const printers = useAppSelector(
    (state: RootState) => state.globalState.printers
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const token = useAppSelector((state: RootState) => state.globalState.token);

  const getDefaultValues = React.useCallback((): FormState => {
    return {
      name: {
        value: printer.name,
        error: "",
      },
      ip: {
        value: printer.ip,
        error: "",
      },
      port: {
        value: printer.port,
        error: "",
      },
    };
  }, [printer.ip, printer.name, printer.port]);
  const [values, setValues] = React.useState<FormState>(getDefaultValues());

  const handleChange = React.useCallback(
    (prop: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => {
        return {
          ...v,
          [prop]: { ...v[prop], value: event.target.value, error: "" },
        };
      });
    },
    []
  );

  const save = React.useCallback(async () => {
    setLoading(true);
    const response = await requestApi({
      method: PATCH,
      path: PRINTERS_URL + "/" + printer.id,
      allowError: true,
      token: token,
      body: {
        name: values.name.value,
        ip: values.ip.value,
        port: values.port.value,
      },
    });
    if (response.statusCode === 200) {
      const newPrinters = [...(printers ?? [])];
      const index = newPrinters.findIndex((x) => x.code === printer.code);
      newPrinters[index] = response.content;
      dispatch(set({ printers: newPrinters }));
    } else if (response.statusCode === 401) {
      toastr.info(t("word.info"), t("error.reconnect"));
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
    setLoading(false);
  }, [
    dispatch,
    printer.code,
    printer.id,
    printers,
    t,
    token,
    values.ip.value,
    values.name.value,
    values.port.value,
  ]);

  React.useEffect(() => {
    setValues(getDefaultValues());
  }, [printer]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TableRow>
      <TableCell>
        <TextField
          autoComplete="off"
          error={!!values.name.error}
          helperText={t(values.name.error ?? "")}
          sx={{ width: "100%" }}
          type="text"
          value={values.name.value}
          onChange={handleChange("name")}
          label={t("field.name")}
        />
      </TableCell>
      <TableCell>
        <TextField
          autoComplete="off"
          error={!!values.ip.error}
          helperText={t(values.ip.error ?? "")}
          sx={{ width: "100%" }}
          type="text"
          value={values.ip.value}
          onChange={handleChange("ip")}
          label={t("word.ip")}
        />
      </TableCell>
      <TableCell>
        <TextField
          autoComplete="off"
          error={!!values.port.error}
          helperText={t(values.port.error ?? "")}
          sx={{ width: "100%" }}
          type="text"
          value={values.port.value}
          onChange={handleChange("port")}
          label={t("word.port")}
        />
      </TableCell>
      <TableCell>
        <CopyClipboardComponent text={printer.code} />
      </TableCell>
      <TableCell>
        <LoadingButton
          variant="text"
          color="inherit"
          sx={{
            borderRadius: "50%",
            minWidth: "auto",
            padding: "8px",
            color: "rgba(0, 0, 0, 0.54)",
          }}
          loading={loading}
          onClick={save}
        >
          <SaveIcon />
        </LoadingButton>
      </TableCell>
    </TableRow>
  );
});

const PrintersComponent: React.FC = React.memo(() => {
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const printers = useAppSelector(
    (state: RootState) => state.globalState.printers
  );
  const printerTemplates = useAppSelector(
    (state: RootState) => state.globalState.printerTemplates
  );
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const { t } = useTranslation();
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);

  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || !force) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      dispatch(set({ printers: undefined, printerTemplates: undefined }));
      const response = await requestApi({
        method: GET,
        path: PRINTERS_URL,
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        dispatch(set({ printers: response.content["hydra:member"] }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      const response2 = await requestApi({
        method: GET,
        path: PRINTER_TEMPLATES_URL,
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response2.statusCode === 200) {
        dispatch(set({ printerTemplates: response2.content["hydra:member"] }));
      } else if (response2.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      dispatch(set({ refreshPage: false }));
    },
    [dispatch, t, token, user]
  );

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [init, refreshPage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Typography variant="h5">{t("word.printers")}</Typography>
      {printers ? (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("word.name")}</TableCell>
                <TableCell>{t("word.ip")}</TableCell>
                <TableCell>{t("word.port")}</TableCell>
                <TableCell>{t("word.code")}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printers.map((printer) => (
                <PrinterFormComponent printer={printer} key={printer.id} />
              ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <SearchComponent nbColumn={1} nbLines={1} />
      )}
      <PrintEmplacementsComponent printer="DEPOT" />
      <Typography sx={{ marginTop: 4 }} variant="h5">
        {t("word.templates")}
      </Typography>
      {printerTemplates ? (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("word.name")}</TableCell>
                <TableCell>{t("word.content")}</TableCell>
                <TableCell>{t("word.code")}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {printerTemplates.map((printerTemplate, indexPrinterTemplate) => (
                <PrinterTemplateFormComponent
                  printerTemplate={printerTemplate}
                  key={indexPrinterTemplate}
                  index={indexPrinterTemplate}
                />
              ))}
            </TableBody>
          </Table>
          <Box sx={{ textAlign: "center" }}>
            <IconButton
              size="large"
              onClick={() => {
                dispatch(
                  set({
                    printerTemplates: [
                      ...printerTemplates,
                      {
                        id: null,
                        name: "",
                        code: "",
                        content: "",
                        deletable: true,
                      },
                    ],
                  })
                );
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </>
      ) : (
        <SearchComponent nbColumn={1} nbLines={1} />
      )}
    </Box>
  );
});

export default PrintersComponent;
